import React, { useEffect, useState, useRef } from "react";
import "./EmailNotification.css";
import api from "../../services/api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import FullscreenModal from "../../components/common/FullscreenModal/FullscreenModal";

// ── SVG Icons ──────────────────────────────────────────────
const SendIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
    <path d="M9 6V4h6v2" />
  </svg>
);

// ── Helper: human-readable file size ───────────────────────
const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

// ── Helper: file icon by extension ─────────────────────────
const getFileIcon = (name) => {
  const ext = name.split(".").pop().toLowerCase();
  if (["pdf"].includes(ext)) return "📄";
  if (["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext)) return "🖼️";
  if (["doc", "docx"].includes(ext)) return "📝";
  if (["xls", "xlsx", "csv"].includes(ext)) return "📊";
  if (["zip", "rar", "7z"].includes(ext)) return "📦";
  return "📎";
};

/* ============================================================
   COMPOSE FORM  (rendered inside FullscreenModal)
   ============================================================ */
const ComposeEmailForm = ({ onSubmitted, onCancel }) => {
  const [subject, setSubject] = useState("");
  const [htmlBody, setHtmlBody] = useState("");
  const [recipientQuery, setRecipientQuery] = useState("");
  const [ccEmails, setCcEmails] = useState([]);
  const [ccInput, setCcInput] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);

  // Dropdown data
  const [queryTypes, setQueryTypes] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    api.get("/notification-query")
      .then((res) => setQueryTypes(res.data || []))
      .catch((err) => console.error("Error fetching query types:", err));
  }, []);

  // ── CC helpers ──────────────────────────────────────────
  const addCcEmail = (raw) => {
    const email = raw.trim().toLowerCase();
    if (!email) return;
    // Basic email validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Invalid email address");
      return;
    }
    if (ccEmails.includes(email)) {
      toast.warn("Email already added");
      return;
    }
    setCcEmails((prev) => [...prev, email]);
    setCcInput("");
  };

  const removeCcEmail = (email) => {
    setCcEmails((prev) => prev.filter((e) => e !== email));
  };

  const handleCcKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addCcEmail(ccInput);
    }
    if (e.key === "Backspace" && !ccInput && ccEmails.length > 0) {
      setCcEmails((prev) => prev.slice(0, -1));
    }
  };

  // ── Attachment helpers ─────────────────────────────────
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setAttachments((prev) => [...prev, ...files]);
    e.target.value = "";
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  // ── Submit ─────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject.trim()) { toast.error("Subject is required"); return; }
    if (!recipientQuery) { toast.error("Please select recipients"); return; }
    if (!htmlBody.trim() || htmlBody === "<p><br></p>") { toast.error("Email body is required"); return; }

    try {
      setSending(true);

      const formData = new FormData();
      formData.append("subject", subject);
      formData.append("html_body", htmlBody);
      formData.append("recipient_query", recipientQuery);
      formData.append("cc_emails", JSON.stringify(ccEmails));

      attachments.forEach((file) => {
        formData.append("attachments", file);
      });

      await api.post("/email-notifications/send", formData);
      toast.success("✅ Email sent successfully!");
      if (onSubmitted) onSubmitted();
    } catch (error) {
      const msg = error?.message || "Failed to send email";
      toast.error("❌ " + msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ position: "relative" }}>
      {sending && <div className="email-loading-overlay">Sending email…</div>}

      <form className="email-compose-form" onSubmit={handleSubmit}>

        <div className="form-row">
          {/* Recipients (Query Type) */}
          <div className="form-group">
            <label>To (Recipients) <span style={{ color: "var(--color-danger)" }}>*</span></label>
            <select
              value={recipientQuery}
              onChange={(e) => setRecipientQuery(e.target.value)}
              required
            >
              <option value="" disabled>Select recipient group</option>
              {queryTypes.map((qt) => (
                <option key={qt.id} value={qt.id}>{qt.query_title}</option>
              ))}
            </select>
          </div>

          {/* Notification Type */}
          <div className="form-group">
            <label>Notification Type</label>
            <select defaultValue="">
              <option value="">General</option>
              <option value="marketing">Marketing</option>
              <option value="transactional">Transactional</option>
              <option value="announcement">Announcement</option>
              <option value="alert">Alert</option>
            </select>
          </div>
        </div>

        {/* CC Emails */}
        <div className="form-group full-width">
          <label>CC</label>
          <div className="cc-input-wrapper" onClick={() => document.getElementById("cc-email-input")?.focus()}>
            {ccEmails.map((email) => (
              <span className="cc-chip" key={email}>
                {email}
                <button type="button" onClick={() => removeCcEmail(email)}>×</button>
              </span>
            ))}
            <input
              id="cc-email-input"
              className="cc-input"
              type="email"
              value={ccInput}
              onChange={(e) => setCcInput(e.target.value.replace(",", ""))}
              onKeyDown={handleCcKeyDown}
              onBlur={() => { if (ccInput.trim()) addCcEmail(ccInput); }}
              placeholder={ccEmails.length === 0 ? "Type email and press Enter" : ""}
            />
          </div>
          <span className="cc-hint">Press Enter or comma to add. Backspace to remove last.</span>
        </div>

        {/* Subject */}
        <div className="form-group full-width">
          <label>Subject <span style={{ color: "var(--color-danger)" }}>*</span></label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Enter email subject"
            required
          />
        </div>

        {/* HTML Body */}
        <div className="form-group full-width quill-editor-group">
          <label>Email Body <span style={{ color: "var(--color-danger)" }}>*</span></label>
          <ReactQuill
            theme="snow"
            value={htmlBody}
            onChange={setHtmlBody}
            placeholder="Compose your email content here…"
            modules={{
              toolbar: [
                [{ header: [1, 2, 3, false] }],
                ["bold", "italic", "underline", "strike", "blockquote"],
                [{ list: "ordered" }, { list: "bullet" }],
                ["link", "image"],
                [{ color: [] }, { background: [] }],
                [{ align: [] }],
                ["clean"],
              ],
            }}
          />
        </div>

        {/* Attachments */}
        <div className="form-group full-width">
          <label>Attachments</label>
          <div
            className="attachment-zone"
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="attachment-zone-content">
              <span className="upload-icon">📎</span>
              <span>Click to add attachments</span>
              <small>PDF, Images, Documents, Spreadsheets etc.</small>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              style={{ display: "none" }}
              onChange={handleFileSelect}
            />
          </div>

          {attachments.length > 0 && (
            <div className="attachment-list">
              {attachments.map((file, idx) => (
                <div className="attachment-item" key={idx}>
                  <span className="attachment-icon">{getFileIcon(file.name)}</span>
                  <div className="attachment-info">
                    <div className="attachment-name">{file.name}</div>
                    <div className="attachment-size">{formatFileSize(file.size)}</div>
                  </div>
                  <button
                    type="button"
                    className="attachment-remove"
                    onClick={() => removeAttachment(idx)}
                    title="Remove"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="email-compose-actions">
          <button type="button" className="email-cancel-btn" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="email-draft-btn" disabled={sending} onClick={() => {toast.success("Draft saved successfully!"); onCancel();}}>
            Save Draft
          </button>
          <button type="submit" className="email-send-btn" disabled={sending}>
            <SendIcon /> Send Email
          </button>
        </div>
      </form>
    </div>
  );
};

/* ============================================================
   MAIN PAGE — sent-emails table + compose modal
   ============================================================ */
const EmailNotification = () => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isComposeOpen, setIsComposeOpen] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const limit = 20;

  const fetchEmails = async (search = false) => {
    setLoading(true);
    try {
      const params = { limit, page };
      if (search) {
        if (fromDate) params.fromDate = fromDate;
        if (toDate) params.toDate = toDate;
      }

      const response = await api.get("/email-notifications", { params });
      setEmails(response.data || []);
      setTotalPages(response.pagination?.totalPages || 1);
    } catch (error) {
      console.error("Error fetching email notifications:", error);
      // Don't toast on load — endpoint may not exist yet
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmails();
  }, [page]);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this email record?")) return;
    try {
      await api.delete(`/email-notifications/${id}`);
      toast.success("Email record deleted");
      fetchEmails();
    } catch (error) {
      toast.error(error?.message || "Failed to delete");
    }
  };

  const handleResend = async (id) => {
    if (!window.confirm("Resend this email?")) return;
    try {
      await api.post(`/email-notifications/${id}/resend`);
      toast.success("Email resent successfully!");
      fetchEmails();
    } catch (error) {
      toast.error(error?.message || "Failed to resend");
    }
  };

  return (
    <div className="email-notif-container">
      <ToastContainer position="bottom-right" autoClose={2000} />

      {/* Header */}
      <div className="email-notif-header">
        <div className="email-notif-header-left">
          <h1>Email Notifications</h1>
          <p>Compose and manage email broadcasts</p>
        </div>
        <button
          className="email-notif-compose-btn"
          onClick={() => setIsComposeOpen(true)}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>edit</span>
          Compose Email
        </button>
      </div>

      {/* Filters */}
      <div className="email-notif-filters">
        <div className="email-notif-filter-group">
          <label>From</label>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </div>
        <div className="email-notif-filter-group">
          <label>To</label>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </div>
        <button
          className="email-notif-search-btn"
          style={{ marginTop: "auto" }}
          onClick={() => { setPage(1); fetchEmails(true); }}
        >
          Search
        </button>
      </div>

      {/* Table */}
      <div className="email-notif-table-wrap">
        <table className="email-notif-table">
          <thead>
            <tr>
              <th style={{ width: 60 }}>ID</th>
              <th>Subject</th>
              <th>Recipients</th>
              <th>CC</th>
              <th>Status</th>
              <th>Sent On</th>
              <th style={{ width: 100 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && emails.length === 0 ? (
              <tr>
                <td colSpan={7} className="email-notif-empty-state">Loading email notifications…</td>
              </tr>
            ) : emails.length > 0 ? (
              emails.map((row) => (
                <tr key={row.id}>
                  <td>{row.id}</td>
                  <td className="subject-cell">{row.subject}</td>
                  <td className="recipients-cell">{row.recipient_query_title || row.recipient_query || "—"}</td>
                  <td>
                    {row.cc_emails && JSON.parse(row.cc_emails || "[]").length > 0 ? (
                      <div className="email-notif-cc-chips-sm">
                        {JSON.parse(row.cc_emails).map((cc) => (
                          <span className="email-notif-cc-chip-sm" key={cc}>{cc}</span>
                        ))}
                      </div>
                    ) : "—"}
                  </td>
                  <td>
                    <span className={`email-status-badge ${row.status || "sent"}`}>
                      {(row.status || "sent").toUpperCase()}
                    </span>
                  </td>
                  <td>
                    {row.sent_at ? (
                      <>
                        <div>{new Date(row.sent_at).toLocaleDateString("en-IN")}</div>
                        <div className="email-notif-time-sub">{new Date(row.sent_at).toLocaleTimeString("en-IN")}</div>
                      </>
                    ) : "—"}
                  </td>
                  <td>
                    <div className="email-notif-actions-cell">
                      <button
                        className="email-notif-icon-btn resend"
                        onClick={() => handleResend(row.id)}
                        title="Resend"
                      >
                        <SendIcon />
                      </button>
                      <button
                        className="email-notif-icon-btn delete"
                        onClick={() => handleDelete(row.id)}
                        title="Delete"
                      >
                        <TrashIcon />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} className="email-notif-empty-state">No email notifications found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="email-notif-pagination">
          <div className="email-notif-pagination-info">
            Showing page {page} of {totalPages}
          </div>
          <div className="email-notif-pagination-controls">
            <button
              className="email-notif-page-btn"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              &lt;
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => {
              if (p === 1 || p === totalPages || (p >= page - 1 && p <= page + 1)) {
                return (
                  <button
                    key={p}
                    className={`email-notif-page-btn ${page === p ? "active" : ""}`}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </button>
                );
              }
              if (p === page - 2 || p === page + 2) {
                return <span key={p} className="email-notif-pagination-ellipsis">…</span>;
              }
              return null;
            })}
            <button
              className="email-notif-page-btn"
              disabled={page === totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              &gt;
            </button>
          </div>
        </div>
      )}

      {/* Compose Modal */}
      {isComposeOpen && (
        <FullscreenModal
          isOpen={isComposeOpen}
          onClose={() => setIsComposeOpen(false)}
          title="Compose Email Notification"
          maxWidth="910px"
        >
          <ComposeEmailForm
            onSubmitted={() => {
              setIsComposeOpen(false);
              fetchEmails();
            }}
            onCancel={() => setIsComposeOpen(false)}
          />
        </FullscreenModal>
      )}
    </div>
  );
};

export default EmailNotification;
